import React, { useEffect, useState } from "react"
import { Box, Flex, HStack } from "@chakra-ui/react"
import EventPage, { EventPageHeader } from "components/layout/EventPage"
import HorizontalStat from "components/elements/HorizontalStat"
import Button from "components/Buttons/Button"
import useModal from "services/useModal"
import useFeatureFlag from "services/featureFlags"
import pluralize from "pluralize"
import {
  AccountUser,
  DioboxEvent,
  EventCounts,
  Form,
  ModalName,
  TabButton,
  FormsSubpath,
} from "sharedTypes"
import {
  Route,
  Switch,
  useLocation,
  useParams,
} from "react-router-dom"
import { errorToast, successToast } from "utilities/toasts"
import { eventFormsPath } from "utilities/routes"
import { MAX_FORMS_PER_EVENT } from "utilities/constants"
import HeaderTabs from "components/elements/HeaderTabs"
import NavigationTabButton from "components/Navigation/HeaderTabs/NavigationTabButton"
import { useForms } from "queries/forms"
import { moveElement, sortByOrderList } from "utilities/objects"
import { postFormsOrder } from "api/forms"
import { refetchForms } from "context/actions"
import { useQueryClient } from "react-query"
import Spinner from "components/elements/Spinner"
import { DropResult } from "react-beautiful-dnd"
import SharePopover from "./SharePopover"
import EventForms from "./FormManagement/EventForms/EventForms"
import EditOrder from "./EditOrder"
import CapacityStat, { SetCapacity } from "./CapacityStat"
import StyleAndConfiguration from "./StyleAndConfiguration/StyleAndConfiguration"

interface Props {
  event: DioboxEvent
  counts: EventCounts
  updateEvent: (params: {}) => Promise<void>
  accountUsers: AccountUser[]
}

const Forms = ({
  event: { id: eventId },
  event,
  counts,
  updateEvent,
  accountUsers,
}: Props) => {
  const showModal = useModal()
  const location = useLocation()
  const { vanityUrls: vanityUrlsEnabled, eventCapacity: eventCapacityEnabled } = useFeatureFlag()
  const { subpath } = useParams<{ subpath?: string }>()
  const { forms: formsCount } = counts
  const [eventWorkingCopy, setEventWorkingCopy] = useState(event)
  const [sortingEnabled, setSorting] = useState(false)
  const { isLoading, data } = useForms(eventId)
  const queryClient = useQueryClient()

  const forms = data?.data
  const initialOrder = forms?.map((form) => form.id) ?? []
  const [order, setOrder] = useState(initialOrder)

  useEffect(() => {
    setOrder(initialOrder)
  }, [forms])

  useEffect(() => {
    setEventWorkingCopy(event)
  }, [
    event.portalEventImageUrl, event.portalLogoUrl, event.translations, event.portalPrimaryColor,
    event.portalTopNavBackgroundColor, event.portalTopNavTextColor, event.portalBackgroundColor,
  ])

  if (isLoading) {
    return <Spinner />
  }

  if (!forms) {
    return null
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) {
      return
    }

    setOrder(moveElement(order, source.index, destination.index))
  }

  const sortedForms = sortByOrderList<Form>(forms, order)

  const handleCancelSorting = () => {
    setSorting(false)
    setOrder(initialOrder)
  }

  const handleSaveSorting = async () => {
    setSorting(false)
    try {
      await postFormsOrder(eventId, order)
    } catch (error) {
      setOrder(initialOrder)
    }

    await refetchForms(queryClient, eventId)
    successToast({ title: "Form order saved." })
  }

  const handleClick = () => {
    if (formsCount >= MAX_FORMS_PER_EVENT) {
      errorToast({
        title: `You can create up to ${MAX_FORMS_PER_EVENT} Forms per event`,
      })

      return
    }

    showModal(ModalName.FormForm)
  }

  const handleResetEventWorkingCopy = () => setEventWorkingCopy(event)

  const tabs = [{
    id: "form-management",
    title: "Form Management",
    iconName: "ticketing-orders",
  },
  ...(vanityUrlsEnabled ? [] : [{
    id: "style-and-configuration",
    title: "Style & Configuration",
    subpath: FormsSubpath.StyleAndConfiguration,
    iconName: "guest-search-settings",
  }])]

  // If there's no active tab, default to the first tab
  const activeTab = tabs.find((tab) => tab.subpath === subpath) ?? tabs[0]

  const handleChangeEventWorkingCopy = (values: Partial<DioboxEvent>) => {
    setEventWorkingCopy((oldEventWorkingCopy) => ({ ...oldEventWorkingCopy, ...values }))
  }

  const eventPageHeaderItems = (
    <>
      {activeTab.id === "form-management" ? (
        <>
          {!sortingEnabled && (
            <HStack spacing={10}>
              {eventCapacityEnabled && (
                <Box as="button" type="button" onClick={() => showModal(ModalName.EventCapacity)}>
                  {event.capacity ? (
                    <CapacityStat
                      capacity={event.capacity}
                      confirmedGuests={counts.confirmedGuests}
                    />
                  ) : <SetCapacity />}
                </Box>
              )}
              <HorizontalStat label={pluralize("Form", formsCount)} value={formsCount} />
            </HStack>
          )}
          <HStack spacing={4} align="center">
            <EditOrder
              sortingEnabled={sortingEnabled}
              onEnableSorting={() => { setSorting(true) }}
              onCancelSorting={handleCancelSorting}
              onSaveSorting={handleSaveSorting}
            />
            {!sortingEnabled && (
              <Button leftIcon="plus" onClick={handleClick} size="sm" data-testid="create-form-button">
                Form
              </Button>
            )}
          </HStack>
        </>
      ) : (
        <SharePopover event={event} />
      )}
    </>
  )

  return (
    <EventPage
      scrollableBody={false}
      header={(
        <EventPageHeader
          icon="forms"
          title="Forms"
          subtitle={tabs.length > 1 && (
            <HeaderNav eventId={eventId} tabs={tabs} activeTab={activeTab} />
          )}
          items={eventPageHeaderItems}
        />
      )}
      body={(
        <Flex h="full" justifyContent="center">
          <Switch location={location}>
            {!vanityUrlsEnabled && (
              <Route path={`${eventFormsPath(eventId)}/${FormsSubpath.StyleAndConfiguration}/:subpath?`}>
                <StyleAndConfiguration
                  event={event}
                  updateEvent={updateEvent}
                  onChangeEventWorkingCopy={handleChangeEventWorkingCopy}
                  eventWorkingCopy={eventWorkingCopy}
                  forms={forms}
                  accountUsers={accountUsers}
                  onResetEventWorkingCopy={handleResetEventWorkingCopy}
                />
              </Route>
            )}
            <Route path={`${eventFormsPath(eventId)}/:formId?`}>
              <EventForms
                event={event}
                counts={counts}
                sortingEnabled={sortingEnabled}
                forms={sortedForms}
                onDragEnd={onDragEnd}
              />
            </Route>
          </Switch>
        </Flex>
      )}
    />
  )
}

type HeaderNavProps = {
  eventId: string
  tabs: TabButton<FormsSubpath>[]
  activeTab?: TabButton<FormsSubpath>
}

const HeaderNav = ({ eventId, tabs, activeTab }: HeaderNavProps) => (
  <HeaderTabs>
    {tabs.map(({
      id, title, subpath, iconName,
    }) => (
      <NavigationTabButton
        key={subpath}
        title={title}
        iconName={iconName}
        active={activeTab?.id === id}
        path={eventFormsPath(eventId, subpath)}
      />
    ))}
  </HeaderTabs>
)

export default Forms
